// Memory-only cache для экрана ввода OTP-кода (S-7 /verify-code).
// Source of truth: docs/specs/auth-otp-verification/spec.md §4.2.
//
// OTP-флоу (D2/D7): пароль БОЛЬШЕ НЕ хранится в памяти — токен выдаёт
// verify-code напрямую. Контекст несёт только {email, mode, verificationChallenge?}.
// Для signin (mode='signin') challenge приходит из 403-ответа login'а (D5) и
// требуется verify-code/resend-code, чтобы код не был passwordless.
//
// SECURITY: только React state (in-memory), НЕ localStorage / Preferences.
// Reload вкладки → state теряется → /verify-code fallback redirect на /signin.
// Idle 30 минут → Provider чистит сам.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type VerifyMode = 'signup' | 'signin';

export type AwaitVerifyState = {
  mode: VerifyMode;
  email: string;
  verificationChallenge?: string;
};

export type AwaitVerifyContextValue = {
  state: AwaitVerifyState | null;
  set: (s: AwaitVerifyState) => void;
  clear: () => void;
};

const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

const AwaitVerifyContext = createContext<AwaitVerifyContextValue | null>(null);

export function AwaitVerifyProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AwaitVerifyState | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const set = useCallback(
    (s: AwaitVerifyState) => {
      setState(s);
      clearTimer();
      timeoutRef.current = setTimeout(() => setState(null), IDLE_TIMEOUT_MS);
    },
    [clearTimer],
  );

  const clear = useCallback(() => {
    setState(null);
    clearTimer();
  }, [clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  const value = useMemo<AwaitVerifyContextValue>(() => ({ state, set, clear }), [state, set, clear]);

  return <AwaitVerifyContext.Provider value={value}>{children}</AwaitVerifyContext.Provider>;
}

export function useAwaitVerifyContext(): AwaitVerifyContextValue {
  const ctx = useContext(AwaitVerifyContext);
  if (!ctx) {
    throw new Error('useAwaitVerifyContext must be used within <AwaitVerifyProvider>');
  }
  return ctx;
}
